// Bible service for fetching verses and managing Bible data
class BibleService {
  constructor() {
    this.cache = new Map();
    this.apiKey = '284adfd57a2f44878f7e042a57021fb3';
    this.apiBaseUrl = 'https://api.scripture.api.bible/v1';
    this.bibles = null;
  }

  async getBibles() {
    if (this.bibles) return this.bibles;

    try {
      const response = await fetch(`${this.apiBaseUrl}/bibles`, {
        headers: {
          'api-key': this.apiKey
        }
      });

      if (!response.ok) {
        throw new Error(`API request failed: ${response.status}`);
      }

      const data = await response.json();
      this.bibles = data.data;
      return this.bibles;
    } catch (error) {
      console.error('Error fetching bibles:', error);
      return [];
    }
  }

  getBibleId(translation = 'KJV') {
    const bibleMap = {
      'KJV': 'de4e12af7f28f599-01', // King James Version
      'NIV': '78a9f6124f3448c5-01', // New International Version
      'ESV': '592420522e16049f-01', // English Standard Version
      'NKJV': 'de4e12af7f28f599-02', // New King James Version
      'NLT': '82b1b8a8c334d9bd-01', // New Living Translation
      'AMP': 'a556c4a7b4e67ad4-01', // Amplified Bible
      'MSG': '065be0da02d0e23f-01', // The Message
      'CSB': '2ef0b03b8f7549bb-01', // Christian Standard Bible
      'NASB': 'b8ee27c2b52b5c88-01' // New American Standard Bible
    };

    return bibleMap[translation.toUpperCase()] || bibleMap['KJV'];
  }

  async getVerse(book, chapter, verse, translation = 'KJV') {
    const cacheKey = `${book}-${chapter}-${verse}-${translation}`;

    if (this.cache.has(cacheKey)) {
      return this.cache.get(cacheKey);
    }

    try {
      const bibleId = this.getBibleId(translation);
      // Convert book name to abbreviation for API
      const bookAbbrev = this.getBookAbbreviation(book);
      const verseId = `${bookAbbrev}.${chapter}.${verse}`;

      const response = await fetch(
        `${this.apiBaseUrl}/bibles/${bibleId}/verses/${verseId}?include-chapter-numbers=false&include-verse-numbers=false`,
        {
          headers: {
            'api-key': this.apiKey
          },
          // Add timeout to prevent hanging requests
          signal: AbortSignal.timeout(10000) // 10 second timeout
        }
      );

      if (!response.ok) {
        if (response.status === 404) {
          // Verse doesn't exist - return null to indicate end of chapter
          return null;
        }
        const errorText = await response.text();
        throw new Error(`API request failed: ${response.status} - ${errorText}`);
      }

      const data = await response.json();

      // Clean HTML content to extract plain text
      const cleanContent = data.data.content
        .replace(/<[^>]*>/g, '') // Remove HTML tags
        .replace(/\s+/g, ' ') // Normalize whitespace
        .trim();

      const verseData = {
        text: cleanContent,
        reference: data.data.reference,
        translation: translation,
        book: book,
        chapter: chapter,
        verse: verse
      };

      this.cache.set(cacheKey, verseData);
      return verseData;

    } catch (error) {
      if (error.name === 'TimeoutError') {
        console.error('Request timeout for verse:', book, chapter, verse);
      } else {
        console.error('Error fetching verse:', error);
      }

      // Return null for missing verses instead of error text
      return null;
    }
  }

  getBookAbbreviation(bookName) {
    const bookMap = {
      'Genesis': 'GEN',
      'Exodus': 'EXO',
      'Leviticus': 'LEV',
      'Numbers': 'NUM',
      'Deuteronomy': 'DEU',
      'Joshua': 'JOS',
      'Judges': 'JDG',
      'Ruth': 'RUT',
      '1 Samuel': '1SA',
      '2 Samuel': '2SA',
      '1 Kings': '1KI',
      '2 Kings': '2KI',
      '1 Chronicles': '1CH',
      '2 Chronicles': '2CH',
      'Ezra': 'EZR',
      'Nehemiah': 'NEH',
      'Esther': 'EST',
      'Job': 'JOB',
      'Psalm': 'PSA',
      'Proverbs': 'PRO',
      'Ecclesiastes': 'ECC',
      'Song of Solomon': 'SNG',
      'Isaiah': 'ISA',
      'Jeremiah': 'JER',
      'Lamentations': 'LAM',
      'Ezekiel': 'EZK',
      'Daniel': 'DAN',
      'Hosea': 'HOS',
      'Joel': 'JOL',
      'Amos': 'AMO',
      'Obadiah': 'OBA',
      'Jonah': 'JON',
      'Micah': 'MIC',
      'Nahum': 'NAM',
      'Habakkuk': 'HAB',
      'Zephaniah': 'ZEP',
      'Haggai': 'HAG',
      'Zechariah': 'ZEC',
      'Malachi': 'MAL',
      'Matthew': 'MAT',
      'Mark': 'MRK',
      'Luke': 'LUK',
      'John': 'JHN',
      'Acts': 'ACT',
      'Romans': 'ROM',
      '1 Corinthians': '1CO',
      '2 Corinthians': '2CO',
      'Galatians': 'GAL',
      'Ephesians': 'EPH',
      'Philippians': 'PHP',
      'Colossians': 'COL',
      '1 Thessalonians': '1TH',
      '2 Thessalonians': '2TH',
      '1 Timothy': '1TI',
      '2 Timothy': '2TI',
      'Titus': 'TIT',
      'Philemon': 'PHM',
      'Hebrews': 'HEB',
      'James': 'JAS',
      '1 Peter': '1PE',
      '2 Peter': '2PE',
      '1 John': '1JN',
      '2 John': '2JN',
      '3 John': '3JN',
      'Jude': 'JUD',
      'Revelation': 'REV'
    };

    return bookMap[bookName] || bookName.substring(0, 3).toUpperCase();
  }

  async getChapter(book, chapter, translation = 'KJV') {
    console.log('Getting chapter:', book, chapter, translation);

    try {
      const bibleId = this.getBibleId(translation);
      const bookAbbrev = this.getBookAbbreviation(book);
      const chapterId = `${bookAbbrev}.${chapter}`;

      console.log('Fetching chapter:', `${this.apiBaseUrl}/bibles/${bibleId}/chapters/${chapterId}`);

      const response = await fetch(
        `${this.apiBaseUrl}/bibles/${bibleId}/chapters/${chapterId}?include-chapter-numbers=false&include-verse-numbers=false`,
        {
          headers: {
            'api-key': this.apiKey
          }
        }
      );

      console.log('Chapter response status:', response.status);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Chapter API Error:', response.status, errorText);
        throw new Error(`API request failed: ${response.status} - ${errorText}`);
      }

      const data = await response.json();
      console.log('Chapter API Response:', data);

      // The API returns all verses as a single HTML block
      // We need to split this into individual verses
      const content = data.data.content;
      console.log('Chapter content preview:', content.substring(0, 200));

      // Remove HTML tags and split by verse markers
      // API.Bible typically uses superscript numbers or other markers
      const cleanText = content
        .replace(/<[^>]*>/g, '') // Remove HTML tags
        .replace(/\s+/g, ' ') // Normalize whitespace
        .trim();

      console.log('Clean text preview:', cleanText.substring(0, 200));

      // Since the API returns continuous text, we need to fall back to individual verses
      // This is the most reliable approach for API.Bible
      console.log('Using individual verse fetching for chapter');
      return this.getVersesIndividually(book, chapter, translation);
    } catch (error) {
      console.error('Error fetching chapter:', error);

      // Fallback: try to get individual verses
      console.log('Falling back to individual verse fetching');
      return this.getVersesIndividually(book, chapter, translation);
    }
  }

  async searchVerses(query, translation = 'KJV', limit = 20) {
    try {
      const bibleId = this.getBibleId(translation);
      const response = await fetch(
        `${this.apiBaseUrl}/bibles/${bibleId}/search?query=${encodeURIComponent(query)}&limit=${limit}`,
        {
          headers: {
            'api-key': this.apiKey
          }
        }
      );

      if (!response.ok) {
        throw new Error(`API request failed: ${response.status}`);
      }

      const data = await response.json();

      return data.data.verses.map(verse => ({
        book: verse.reference.split(' ')[0],
        chapter: parseInt(verse.reference.split(' ')[1].split(':')[0]),
        verse: parseInt(verse.reference.split(':')[1]),
        text: verse.text,
        reference: verse.reference
      }));

    } catch (error) {
      console.error('Error searching verses:', error);
      // No fallback - return empty array
      return [];
    }
  }

  async getVersesIndividually(book, chapter, translation = 'KJV', maxVerses = 50) {
    const verses = [];
    const batchSize = 3; // Reduced batch size for better reliability

    for (let batchStart = 1; batchStart <= maxVerses; batchStart += batchSize) {
      const batchPromises = [];

      for (let i = batchStart; i < batchStart + batchSize && i <= maxVerses; i++) {
        batchPromises.push(this.getVerse(book, chapter, i, translation));
      }

      try {
        const batchResults = await Promise.allSettled(batchPromises);
        let foundMissingVerse = false;

        for (let i = 0; i < batchResults.length; i++) {
          const result = batchResults[i];
          const verseNum = batchStart + i;

          if (result.status === 'fulfilled') {
            const verse = result.value;
            if (verse && verse.text) {
              verses.push({ verse: verseNum, text: verse.text });
            } else {
              // Null return indicates verse doesn't exist - we've reached the end
              foundMissingVerse = true;
              break;
            }
          } else {
            console.log('Error fetching verse', verseNum, result.reason);
            foundMissingVerse = true;
            break;
          }
        }

        if (foundMissingVerse) {
          break; // Stop if we hit a missing verse
        }

        // Small delay between batches to be respectful to the API
        if (batchStart + batchSize <= maxVerses) {
          await new Promise(resolve => setTimeout(resolve, 200));
        }

      } catch (error) {
        console.log('Error in batch processing:', error);
        break;
      }
    }

    return verses;
  }

  clearCache() {
    this.cache.clear();
  }

  // Get available translations
  getAvailableTranslations() {
    return [
      { code: 'KJV', name: 'King James Version', language: 'English' },
      { code: 'NIV', name: 'New International Version', language: 'English' },
      { code: 'ESV', name: 'English Standard Version', language: 'English' },
      { code: 'NKJV', name: 'New King James Version', language: 'English' },
      { code: 'NLT', name: 'New Living Translation', language: 'English' }
    ];
  }
}

export default new BibleService();